import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';

import {
  Typography, Button, Icon, Switch, FormControlLabel,
} from '@mui/material';

import { StoreUtil, PhoneUtil, DateUtil, SpecialtyUtil } from 'doctivity-shared/utils';
import { withRouter } from 'utils';
import {
  DynamicTable, FindProviderDialog, AffiliatedIcon, InactiveIcon, TitleTooltip,
} from 'components';
import axiosInstance from 'utils/axiosUtil';
import fileDownload from 'js-file-download';
import { format } from 'date-fns';

import { queryLocationProviders } from 'store/actions/locationsActions';
import { upsertProvider } from 'store/actions/providersActions';
import { queryTaxonomyCodes } from 'store/actions/taxonomyActions';
import { ConfirmAddProviderDialog } from 'locations';

const styles = (theme) => ({
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  header: {
    marginRight: theme.spacing(2),
    flex: 1,
  },
  leftIcon: {
    marginRight: theme.spacing(1),
  },
  switchControl: {
    marginRight: theme.spacing(1),
  },
  switchLabel: {
    fontSize: 13,
  },
});

// Added and removed independently of whatever the user typed into the column
// filters: DynamicTable hands back a whole rebuilt query on every filter change
// and would otherwise drop it. Same shape as applyActiveFilter in ClientsPage.
//
// inactive_at is a plain column on Providers, so this is an ordinary where
// clause on the queried table — no join to reason about, and it means the same
// thing for every client.
function applyInactiveFilter(query, hideInactive) {
  const updated = { ...query };
  const where = { ...(updated.where || {}) };

  if (hideInactive) {
    where.inactive_at = null;
  } else {
    delete where.inactive_at;
  }

  updated.where = where;
  return updated;
}

// this controls which columns are displayed and how they are looked up in data
const columns = [
  {
    label: '',
    sortable: false,
    showFilter: false,
    key: 'clients.id',
    type: 'bool',
    // Shares the affiliation column rather than taking one of its own. Both are
    // status about the same provider, and a dedicated column would cost every
    // row horizontal space to say nothing on almost all of them — inactive
    // providers are rare.
    format: (value, row) => {
      return (
        <>
          {row?.clients[0]?.id > 0 ? <AffiliatedIcon /> : ''}
          {row?.inactive_at ? <InactiveIcon inactiveAt={row.inactive_at} /> : ''}
        </>
      );
    },
    // Left at the affiliation icon's own width. DynamicTable applies column
    // styles to the header cell only, and the table is auto-layout, so this is
    // a preference: the column grows to fit a second icon on the rows that have
    // one, and costs nothing on a location where nobody is inactive.
    style: { width: 16 },
  },
  {
    label: 'Last Name',
    key: 'last_name',
    fulltext: true,
    style: { minWidth: 100 },
  },
  {
    label: 'First Name',
    key: 'first_name',
    fulltext: true,
    style: { minWidth: 100 },
  },
  {
    label: 'Credential',
    key: 'credential',
    style: { width: 105, minWidth: 105, maxWidth: 105 },
    format: (value) => (
      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: 105 }} title={value}>
        {value}
      </div>
    ),
  },
  {
    label: 'Specialty',
    key: 'taxonomy.classification',
    filterFields: ['primary_taxonomy'],
    format: (value, row) => SpecialtyUtil.getNameFromTaxonomy(row.taxonomy),
    specialtySearch: true,
  },
  {
    label: 'NPI',
    key: 'npi',
    type: 'npi',
  },
  {
    label: 'Email',
    key: 'email',
    sortable: false,
    showFilter: false,
    // Prefer the client-specific email (ProviderClients.email) when present,
    // otherwise fall back to the provider's global email (Providers.email).
    format: (value, row) => row?.provider_client?.email || row?.email || '',
  },
  {
    label: 'Phone',
    key: 'phone',
    format: (phone) => PhoneUtil.formatNumber(phone),
  },
];

class LocationProviders extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      linkProviderOpen: false,
      confirmAddProviderData: undefined,
      query: applyInactiveFilter({
        where: {},
        attributes: ['id', 'first_name', 'last_name', 'credential', 'npi', 'phone', 'email', 'inactive_at'],
        include: [
          {
            association: 'location',
            where: {
              id: props.location.id,
            },
            required: true,
          },
          { association: 'taxonomy' },
          {
            association: 'clients',
            attributes: ['id'],
            required: false,
            duplicating: false,
            through: {
              attributes: [],
              where: {
                client_id: props.clientId,
                is_affiliated: true,
              },
            },
          },
          {
            // Client-specific email override; required:false keeps this a LEFT JOIN
            // (client_id filter goes in the ON clause) so providers without a
            // ProviderClients row for this client are still returned.
            association: 'provider_client',
            attributes: ['email'],
            required: false,
            where: {
              client_id: props.clientId,
            },
          },
        ],
        order: [['last_name', 'ASC']],
        limit: 25,
        offset: 0,
      }, props.hideInactive),
    };
  }

  componentDidMount() {
    const { providers } = this.props;

    if (StoreUtil.needsLoadNoCache(providers)) {
      this.fetchData();
    }

    const values = JSON.parse(sessionStorage.getItem('selectedSpecialties'));
    if (values && values.length > 0) {
      this.fetchSpecialties(values);
    }
  }

  componentDidUpdate(prevProps) {
    const { location, providers, editingProviders, hideInactive } = this.props;

    if (!prevProps.location || location.id !== prevProps.location.id) {
      this.fetchData();
    }

    if (prevProps.hideInactive !== hideInactive) {
      this.setState((prevState) => ({
        query: {
          ...applyInactiveFilter(prevState.query, hideInactive),
          // the result set just changed size, so page one is the only safe page
          offset: 0,
        },
      }), this.fetchData.bind(this));
    }

    if (editingProviders !== prevProps.editingProviders) {
      if (StoreUtil.hasSavedSinceLoad(editingProviders, providers)) {
        this.fetchData();
      }
    }
  }

  fetchData() {
    const { location } = this.props;

    if (location && location.id) {
      this.props.dispatch(
        queryLocationProviders(location.id, this.state.query, this.props.clientId)
      );
    }
  }

  fetchSpecialties(values) {
    const { dispatch } = this.props;
    dispatch(queryTaxonomyCodes({
      where: { code: values },
      order: [['code', 'ASC']],
      limit: 10000,
      offset: 0,
    }));
  }

  render() {
    const {
      classes, providers, clientId, location, taxonomyCodes,
      hideInactive, onHideInactiveChange,
    } = this.props;

    const { query, linkProviderOpen, confirmAddProviderData } = this.state;

    return (
      <>
        <DynamicTable
          header={
            <div className={classes.headerContainer}>
              <TitleTooltip
                title='Providers'
                onExport={this.downloadProviders}
              />
              <div className={classes.header} />

              <FormControlLabel
                className={classes.switchControl}
                control={(
                  <Switch
                    checked={hideInactive}
                    onChange={onHideInactiveChange}
                  />
                )}
                label={(
                  <Typography className={classes.switchLabel}>
                    Hide Inactive Providers
                  </Typography>
                )}
              />

              <Button
                color='primary'
                size='small'
                onClick={this.onOpenLinkProvider}
              >
                <Icon className={classes.leftIcon}>add</Icon>
                Link Provider
              </Button>
            </div>
          }
          showFilters={true}
          noDataMessage='No providers in this location.'
          columns={columns}
          taxonomyCodes={taxonomyCodes}
          query={query}
          data={providers}
          onQueryChange={this.onQueryChange}
          getRowHref={this.getRowHref}
        />
        {linkProviderOpen && (
          <FindProviderDialog
            clientId={clientId}
            open={linkProviderOpen}
            onSelect={this.onProviderSelected}
          />
        )}
        {confirmAddProviderData && (
          <ConfirmAddProviderDialog
            open={!!confirmAddProviderData}
            locationData={location}
            provider={confirmAddProviderData}
            onConfirm={this.onConfirmAddProvider}
            onClose={this.closeConfirmAddProviderDialog}
          />
        )}
      </>
    );
  }

  downloadProviders = async () => {
    const { location, clientId } = this.props;
    try {
      const response = await axiosInstance.get('/Providers', {
        params: {
          opts: {
            where: {},
            attributes: ['id', 'npi', 'first_name', 'last_name', 'credential', 'inactive_at'],
            include: [
              {
                association: 'location',
                where: { id: location.id },
                required: true,
                attributes: ['address1', 'address2', 'city', 'state', 'postal_code'],
              },
              {
                association: 'taxonomy',
                attributes: ['id', 'code', 'specialization', 'classification', 'doctivity_name'],
              },
              {
                association: 'clients',
                attributes: ['id'],
                through: { attributes: [], where: { is_affiliated: true } },
                required: false,
                where: { id: clientId },
              },
            ],
            order: [['last_name', 'ASC']],
            limit: 5000,
            offset: 0,
          },
        },
        timeout: 60000,
      });

      // The CSV honours the Hide Inactive toggle and nothing else — the column
      // filters stay on screen. Split here rather than in the query so the
      // request shape is unchanged from what it has always been.
      const allRows = (response.data?.rows) || [];
      const data = this.props.hideInactive
        ? allRows.filter((row) => !row.inactive_at)
        : allRows;

      const csvData = this.formatProvidersCSV(data);
      const locationName = (location.name || `location_${location.id}`).replace(/[^a-zA-Z0-9]/g, '_');
      const filename = `doctivity_providers_${locationName}_${format(new Date(), 'yy_MM_dd')}.csv`;
      fileDownload(csvData, filename);
    } catch (error) {
      console.error('Error downloading providers:', error);
    }
  };

  formatProvidersCSV = (data) => {
    const headers = [
      'Affiliated',
      'NPI',
      'Last Name',
      'First Name',
      'Credential',
      'Specialty',
      'Address',
      'City',
      'State',
      'Zip',
      'URL',
    ];

    const rows = data.map((provider) => {
      const location = provider.location || {};
      const isAffiliated =
        provider.clients && Array.isArray(provider.clients) && provider.clients.length > 0;

      let address = location.address1 || '';
      if (location.address2) {
        address += address ? ` ${location.address2}` : location.address2;
      }

      const url = `${process.env.WEB_DOMAIN || window.location.origin}/providers/${provider.id}`;

      return [
        isAffiliated ? 'Yes' : 'No',
        provider.npi || '',
        provider.last_name || '',
        provider.first_name || '',
        provider.credential || '',
        SpecialtyUtil.getNameFromTaxonomy(provider.taxonomy) || '',
        address,
        location.city || '',
        location.state || '',
        location.postal_code || '',
        url,
      ];
    });

    const csvRows = [headers, ...rows];
    return csvRows
      .map((row) =>
        row
          .map((field) => {
            const fieldStr = String(field || '');
            if (fieldStr.includes(',') || fieldStr.includes('"') || fieldStr.includes('\n')) {
              return `"${fieldStr.replace(/"/g, '""')}"`;
            }
            return fieldStr;
          })
          .join(','),
      )
      .join('\n');
  };

  onQueryChange = (query) => {
    // DynamicTable rebuilds the whole query when a column filter changes, which
    // drops our clause — put it back before it reaches the API.
    this.setState(
      { query: applyInactiveFilter(query, this.props.hideInactive) },
      this.fetchData.bind(this),
    );
  };

  getRowHref = (row) => {
    return `/providers/${row.id}`;
  };

  onOpenLinkProvider = () => {
    this.setState({
      linkProviderOpen: true,
    });
  };

  onCloseLinkProvider = () => {
    this.setState({
      linkProviderOpen: false,
    });
  };

  onConfirmAddProvider = (provider) => {
    const { location, dispatch } = this.props;

    if (provider && location && location.id) {
      if (provider.location_id !== location.id) {
        provider.location_updated_at = DateUtil.formatNowForDB();
      }
      dispatch(upsertProvider({ ...provider, location_id: location.id }));
    }
    this.onCloseLinkProvider();
  };

  closeConfirmAddProviderDialog = () => {
    this.setState({ confirmAddProviderData: undefined });
  };

  onProviderSelected = (provider) => {
    const { location } = this.props;

    if (provider && location && location.id) {
      this.setState({ confirmAddProviderData: provider });
    } else {
      this.onCloseLinkProvider();
    }
  };
}

LocationProviders.propTypes = {
  router: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  providers: PropTypes.object.isRequired,
  classes: PropTypes.object,
  location: PropTypes.object.isRequired,
  clientId: PropTypes.number.isRequired,
  editingProviders: PropTypes.object,
  taxonomyCodes: PropTypes.object,
  hideInactive: PropTypes.bool,
  onHideInactiveChange: PropTypes.func,
};

LocationProviders.defaultProps = {
  hideInactive: true,
};

function mapStateToProps(state) {
  return {
    clientId: state.app.selectedClient,
    providers: StoreUtil.get(state.locations, 'providers'),
    editingProviders: StoreUtil.get(
      state.providers,
      StoreUtil.COMMON_EDIT_ITEM
    ),
    taxonomyCodes: StoreUtil.get(state.taxonomyCodes, StoreUtil.COMMON_TABLE),
  };
}

const styled = withStyles(styles)(LocationProviders);
const connected = connect(mapStateToProps)(styled);
const routed = withRouter(connected);
export { routed as LocationProviders };